import { useGtm } from '@/composition/useGtm'
import utils from '@/utils'
import * as loginService from '@/service/login'
import * as memberService from '@/service/member'
import { GtmEventMap } from '@/view-models/gtm'
import { useAlertStore } from '@/store-pinia/alert'

export type ThirdLoginProvider = 'facebook' | 'google' | 'apple'

export interface UserContract {
  isTermsAccepted: boolean
  isMarketingAllowed: boolean
}

/** for mixpanel */
const providerLoginTypeMap: Record<ThirdLoginProvider, string> = {
  facebook: 'FB',
  google: 'Google',
  apple: 'Apple',
}

/** for GA4 */
const providerAuthMethodMap: Record<ThirdLoginProvider, string> = {
  facebook: 'facebook',
  google: 'google',
  apple: 'apple',
}

export function useThirdLogin(options: { provider: ThirdLoginProvider; registerOptions: Record<string, boolean>; onSuccess?: () => void }) {
  const nuxtApp = useNuxtApp()
  const { $axios, $store: store } = nuxtApp
  const { sendGtmEvent } = useGtm()

  const alertStore = useAlertStore()

  const regionId = computed(
    () => store.getters['region/region'].id || store.getters['branch/branch'].region_id || store.getters['product/productInfo'].region_id
  )

  const showConsent = ref(false)
  const pendingUserInfo = ref<Record<string, any> | null>(null)

  function onLoginSuccess(uid: string) {
    sendGtmEvent({
      event: GtmEventMap.ACCOUNT_LOGGED_IN,
      payload: {
        'Login Type': providerLoginTypeMap[options.provider],
      },
    })
    sendTrackingCode(uid, GtmEventMap.LOGIN)
    options.onSuccess?.()
  }

  function onNeedRegister(userInfo: Record<string, any>) {
    pendingUserInfo.value = userInfo
    showConsent.value = true
  }

  async function handleConsentConfirm(userContract: UserContract) {
    showConsent.value = false

    if (!pendingUserInfo.value) {
      return
    }

    await registerWithConsent(userContract)
  }

  async function registerWithConsent(userContract: UserContract) {
    try {
      const registerData = {
        ...pendingUserInfo.value,
        osVersion: navigator.userAgent,
        deviceId: utils.UUID,
        userContract,
      }

      const registerResult = await loginService.register($axios as any, registerData as any, options.registerOptions as any)

      if (registerResult.code !== 0) {
        throw registerResult
      }

      await handlePostRegistration(registerResult)

      pendingUserInfo.value = null
      return true
    } catch (error: any) {
      openErrorDialog(error instanceof Error ? error.message : String(error))
      return false
    }
  }

  async function handlePostRegistration(registerResult: any) {
    let userInfo = registerResult.data
    if (regionId.value) {
      await memberService.updateMemberRegion($axios as any, regionId.value)

      userInfo = {
        ...userInfo,
        region_id: regionId.value,
      }
      if (store.getters['authentication/getUser']) {
        store.commit('authentication/UPDATE_USER_REGION_ID', regionId.value)
      }
    }
    nuxtApp.$auth.$storage.setUniversal('user', userInfo)
    store.commit('authentication/SET_USER', userInfo)
    sendTrackingCode(registerResult.data.uid, GtmEventMap.REGISTRATION)
    options.onSuccess?.()
  }

  function sendTrackingCode(UID = '', trackingType = GtmEventMap.REGISTRATION) {
    if (!UID) {
      return
    }

    nuxtApp.$gtm.push({
      event: trackingType,
      authenticationMethod: providerAuthMethodMap[options.provider],
      userId: UID,
    })
  }

  function openErrorDialog(message: string) {
    alertStore.showAlert({ message: message, type: 'error' })
  }

  function resetConsent() {
    showConsent.value = false
    pendingUserInfo.value = null
  }

  return {
    showConsent,
    pendingUserInfo,
    onLoginSuccess,
    onNeedRegister,
    handleConsentConfirm,
    openErrorDialog,
    resetConsent,
  }
}
